import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faArrowRotateLeft, faCircleInfo, faArrowUpFromBracket, faImage } from "@fortawesome/free-solid-svg-icons";
import { motion } from "framer-motion";
import { useParams, useSearchParams } from "react-router-dom";

import CameraCapture from "../components/CameraCapture";
import ImageGallery from "../components/ImageGallery";
import PoweredBy from "../components/PoweredBy";
import { useTranslation } from "react-i18next";
import api from "../api";

const SearchAI = ({ event }) => {
  const { t } = useTranslation();
  const { id_name } = useParams();
  const [searchParams] = useSearchParams();
  const sharedPhoto = searchParams.get("photo");
  const [images, setImages] = useState(() => sharedPhoto ? [{ name: sharedPhoto }] : []);
  const [matchInfo, setMatchInfo] = useState(null);
  const [showMatch, setShowMatch] = useState(!!sharedPhoto);

  // True when viewing a single shared photo (no real face-match session yet)
  const isSharedPhotoView = !!sharedPhoto && !matchInfo;
  const [showTip, setShowTip] = useState(false);
  const [newMatchDone, setNewMatchDone] = useState(false);
  const [showSharePopup, setShowSharePopup] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const showGallery = !!sessionStorage.getItem(`gallery_visited_${id_name}`);

  // Restore session on mount
  useEffect(() => {
    if (!event?.event_id) return;
    const sessionId = localStorage.getItem(`match_session_${event.event_id}`);
    if (!sessionId) return;
    api.get(`/match_session?session_id=${sessionId}`)
      .then((res) => {
        setImages(res.data.images);
        setMatchInfo(res.data.infos);
        setShowMatch(true);
      })
      .catch(() => localStorage.removeItem(`match_session_${event.event_id}`));
  }, [event?.event_id]);

  // Persist session_id to localStorage whenever a new match completes
  useEffect(() => {
    if (matchInfo?.session_id && event?.event_id) {
      localStorage.setItem(`match_session_${event.event_id}`, matchInfo.session_id);
    }
  }, [matchInfo, event?.event_id]);

  // Show share popup 2s after API returns photos — only on new match, not session restore
  useEffect(() => {
    if (!newMatchDone || !showMatch || images.length === 0) return;
    if (localStorage.getItem(`share_cta_shown_${id_name}`)) return;
    const timer = setTimeout(() => setShowSharePopup(true), 2000);
    return () => clearTimeout(timer);
  }, [newMatchDone, showMatch, images, id_name]);

  const handleMatch = (newImage) => {
    setImages(newImage);
    setNewMatchDone(true);
  };

  const handleShare = async () => {
    const url = `${window.location.origin}/event/${id_name}`;
    if (navigator.share) {
      try { await navigator.share({ url }); } catch (_) {}
    } else {
      await navigator.clipboard.writeText(url);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    }
    localStorage.setItem(`share_cta_shown_${id_name}`, "1");
    setShowSharePopup(false);
  };

  const dismissSharePopup = () => {
    localStorage.setItem(`share_cta_shown_${id_name}`, "1");
    setShowSharePopup(false);
  };

  const handleDelete = (index) => {
    const newImages = images.filter((_, i) => i !== index);
    setImages(newImages);
  };

  const handleRetry = () => {
    if (event?.event_id) localStorage.removeItem(`match_session_${event.event_id}`);
    setImages([]);
    setMatchInfo(null);
    setShowMatch(false);
  };

  return (
    event && (
      <>
      <div style={{ padding: "12px 0" }}>

        {/* Camera / upload */}
        {!showMatch && event.nb_images === 0 && (
          <div style={{ textAlign: "center", padding: "64px 24px" }}>
            <FontAwesomeIcon icon={faImage} style={{ fontSize: "28px", color: "#ccc", marginBottom: "14px" }} />
            <p style={{ fontSize: "15px", fontWeight: 600, color: "#111", marginBottom: "6px" }}>
              {t("camera_capture.no_gallery_yet_title")}
            </p>
            <p style={{ fontSize: "13px", color: "#aaa", lineHeight: 1.6, margin: 0 }}>
              {t("camera_capture.no_gallery_yet_body")}
            </p>
          </div>
        )}

        {!showMatch && !!event.nb_images && (
          <>
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: "260px" }}>
              <CameraCapture
                event_id={event.event_id}
                setMatchGallery={handleMatch}
                setShowMatch={setShowMatch}
                setMatchInfo={setMatchInfo}
              />
            </div>
            <div style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              gap: "6px",
              margin: "8px 16px 0",
            }}>
              <p style={{
                fontSize: 11,
                color: "#c0c0c0",
                margin: 0,
                letterSpacing: "0.3px",
                textAlign: "center",
              }}>
                {t("user_panel.gallery_photos_only")}
              </p>

              {/* Info tooltip trigger — only shown if gallery tab is visible */}
              {showGallery && (
              <motion.div
                onMouseEnter={() => setShowTip(true)}
                onMouseLeave={() => setShowTip(false)}
                style={{ position: "relative", lineHeight: 1 }}
                animate={{ scale: [1, 1.5, 1, 1.5, 1] }}
                transition={{ duration: 0.7, delay: 0.4, ease: "easeInOut" }}
              >
                <FontAwesomeIcon
                  icon={faCircleInfo}
                  style={{ fontSize: "15px", color: "#1a1a1a", cursor: "default" }}
                />

                {showTip && (
                  <div style={{
                    position: "absolute",
                    bottom: "calc(100% + 8px)",
                    left: "50%",
                    transform: "translateX(-50%)",
                    background: "#1a1a1a",
                    borderRadius: "10px",
                    padding: "12px 14px",
                    width: "240px",
                    boxShadow: "0 8px 24px rgba(0,0,0,0.18)",
                    zIndex: 100,
                    pointerEvents: "none",
                  }}>
                    <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: "7px" }}>
                      {[
                        t("user_panel.find_me_reassurance.share_friends"),
                        t("user_panel.find_me_reassurance.gallery_hidden"),
                      ].map((item, i) => (
                        <li key={i} style={{ display: "flex", alignItems: "flex-start", gap: "7px", fontSize: "11px", color: "#e0e0e0", lineHeight: "1.5" }}>
                          <span style={{ color: "#4ade80", fontWeight: 700, flexShrink: 0, marginTop: "1px" }}>✓</span>
                          {item}
                        </li>
                      ))}
                    </ul>
                    {/* Arrow */}
                    <div style={{
                      position: "absolute",
                      bottom: "-5px",
                      left: "50%",
                      transform: "translateX(-50%) rotate(45deg)",
                      width: "10px",
                      height: "10px",
                      background: "#1a1a1a",
                      borderRadius: "2px",
                    }} />
                  </div>
                )}
              </motion.div>
              )}
            </div>
          </>
        )}

        {/* Results */}
        {showMatch && (
          <>
            {/* Results header */}
            <div className="container" style={{ padding: "8px 12px 16px" }}>
            {isSharedPhotoView ? (
              <div style={{
                background: "#f5f5f5",
                borderRadius: "10px",
                padding: "10px 14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: "12px",
              }}>
                <div>
                  <p style={{ margin: 0, fontWeight: 600, fontSize: "13px", color: "#111" }}>
                    {t("user_panel.shared_photo.badge")}
                  </p>
                  <p style={{ margin: "2px 0 0", fontSize: "12px", color: "#888" }}>
                    {t("user_panel.shared_photo.cta")}
                  </p>
                </div>
                <button
                  onClick={handleRetry}
                  style={{
                    flexShrink: 0,
                    background: "#111",
                    border: "none",
                    color: "#fff",
                    cursor: "pointer",
                    fontSize: "12px",
                    fontWeight: 600,
                    padding: "7px 12px",
                    borderRadius: "8px",
                    whiteSpace: "nowrap",
                  }}
                >
                  {t("user_panel.shared_photo.find_btn")}
                </button>
              </div>
            ) : (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
              }}
            >
              <div style={{ display: "flex", alignItems: "baseline", gap: "8px" }}>
                <span style={{ fontWeight: 600, fontSize: "15px" }}>
                  {images.length} {t("user_panel.images_found")}
                </span>
                {matchInfo?.nb_analysed != null && (
                  <span style={{ fontSize: "12px", color: "#aaa" }}>
                    / {matchInfo.nb_analysed} {t("user_panel.images_analyzed")}
                  </span>
                )}
              </div>

              <button
                onClick={handleRetry}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  fontSize: "13px",
                  color: "#888",
                  padding: "4px 8px",
                  borderRadius: "6px",
                }}
                onMouseOver={(e) => (e.currentTarget.style.background = "#f5f5f5")}
                onMouseOut={(e) => (e.currentTarget.style.background = "none")}
              >
                <FontAwesomeIcon icon={faArrowRotateLeft} size="sm" />
                {t("user_panel.retry")}
              </button>
            </div>
            )}
            </div>

            {/* No results */}
            {images.length === 0 && (
              <div style={{ textAlign: "center", padding: "60px 24px" }}>
                <p style={{ fontSize: "15px", fontWeight: 500, color: "#555", marginBottom: "6px" }}>
                  {t("user_panel.no_images_found")}
                </p>
                {matchInfo?.nb_analysed > 0 && (
                  <p style={{ fontSize: "12px", color: "#bbb" }}>
                    {matchInfo.nb_analysed} {t("user_panel.images_analyzed")}
                  </p>
                )}
              </div>
            )}

            {/* Gallery */}
            {images.length > 0 && (
              <ImageGallery
                event={event}
                images={images}
                setImages={setImages}
                search={false}
                showShare={true}
                watermarkEnabled={event?.watermark_enabled ?? true}
              />
            )}
          </>
        )}
      </div>
      {/* Share CTA popup — bottom sheet, shown once after first match */}
      {showSharePopup && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            onClick={dismissSharePopup}
            style={{
              position: "fixed",
              inset: 0,
              background: "rgba(0,0,0,0.4)",
              zIndex: 200,
            }}
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            transition={{ type: "spring", damping: 24, stiffness: 280 }}
            style={{
              position: "fixed",
              bottom: 0,
              left: 0,
              right: 0,
              background: "#fff",
              borderRadius: "20px 20px 0 0",
              padding: "12px 24px 44px",
              zIndex: 201,
              textAlign: "center",
            }}
          >
            <div style={{ width: 36, height: 4, background: "#e0e0e0", borderRadius: 2, margin: "0 auto 20px" }} />
            <p style={{ fontWeight: 700, fontSize: "17px", margin: "0 0 6px", color: "#111" }}>
              {t("user_panel.share_popup.title")}
            </p>
            <p style={{ fontSize: "13px", color: "#888", margin: "0 0 24px", lineHeight: 1.6 }}>
              {t("user_panel.share_popup.body")}
            </p>
            <button
              onClick={handleShare}
              style={{
                width: "100%",
                padding: "14px",
                background: "#111",
                color: "#fff",
                border: "none",
                borderRadius: "12px",
                fontSize: "15px",
                fontWeight: 600,
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: "8px",
              }}
            >
              <FontAwesomeIcon icon={faArrowUpFromBracket} />
              {shareCopied ? t("user_panel.share_popup.copied") : t("user_panel.share_popup.btn")}
            </button>
            <button
              onClick={dismissSharePopup}
              style={{
                marginTop: "12px",
                background: "none",
                border: "none",
                fontSize: "13px",
                color: "#bbb",
                cursor: "pointer",
                padding: "4px",
              }}
            >
              {t("user_panel.share_popup.dismiss")}
            </button>
          </motion.div>
        </>
      )}

      <PoweredBy />
      </>
    )
  );
};

export default SearchAI;
